import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../../utils/ThemeSwitcher';

// Quality-scale palette — aligned with DataQuad's green→amber→red status system.
// Normal bars read as "clean / in-range"; outlier bars read as "needs review".
// Reference lines use brand-aligned colors that don't compete with the green/red.
const PALETTE = {
  normalFill: '#10B981',       // emerald-500 — clean
  normalStroke: '#059669',     // emerald-600
  outlierFill: '#EF4444',      // red-500 — outlier
  outlierStroke: '#DC2626',    // red-600
  meanLine: '#D97706',         // amber-600 (brand gold)
  medianLine: '#6366F1',       // indigo-500 — neutral, distinct from quality scale
};

// Compact number formatter — keeps axis labels short (e.g. 1,500,000 → 1.5M, 250 → 250, 0.012 → 0.01).
// Used by both the x-axis ticks and the tooltip's range display so the chart reads consistently.
const smartFormat = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '';
  if (v === 0) return '0';
  const abs = Math.abs(v);
  const compact = (divisor, suffix) => {
    const scaled = v / divisor;
    const rounded = Math.round(scaled * 10) / 10;
    return (Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)) + suffix;
  };
  if (abs >= 1e12) return compact(1e12, 'T');
  if (abs >= 1e9)  return compact(1e9,  'B');
  if (abs >= 1e6)  return compact(1e6,  'M');
  if (abs >= 1e3)  return compact(1e3,  'K');
  if (abs >= 100)  return Math.round(v).toString();
  if (abs >= 1)    return v.toFixed(1).replace(/\.0$/, '');
  return v.toFixed(2);
};

const HistogramChart = ({ data, selectedVariable, width: propWidth = 600, height = 400 }) => {
  const ref = useRef();
  const containerRef = useRef();
  const { isDarkMode } = useTheme();
  const [containerWidth, setContainerWidth] = useState(propWidth);

  // Responsive width from container
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth > 0) setContainerWidth(newWidth);
      }
    };
    updateWidth();
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          const newWidth = entries[0].contentRect.width;
          if (newWidth > 0) setContainerWidth(newWidth);
        }
      });
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updateWidth);
    const timeout = setTimeout(updateWidth, 100);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timeout);
    };
  }, []);

  const width = Math.min(propWidth, containerWidth);
  const isCompact = width < 400;

  useEffect(() => {
    if (!data || !selectedVariable || !ref.current) return;

    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();

    const variableData = data[selectedVariable];
    if (!variableData || variableData.error) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('fill', PALETTE.outlierStroke)
        .text(variableData?.error || 'No data available for this variable');
      return;
    }

    // Tighter margins on compact widths so the plot area stays readable on mobile.
    // bottom is provisional — we may grow it below if x-axis labels need to rotate.
    const margin = {
      top: 32,
      right: isCompact ? 16 : 24,
      bottom: 52,
      left: isCompact ? 44 : 56,
    };
    const innerWidth = width - margin.left - margin.right;
    // innerHeight computed below, after we decide whether to rotate x labels

    // Use pre-calculated histogram data if available, otherwise fall back to raw values
    let bins, xDomain, yMax;

    if (variableData.histogram && variableData.histogram.counts) {
      const histData = variableData.histogram;
      bins = histData.counts.map((count, i) => ({
        count,
        x0: histData.bin_edges[i],
        x1: histData.bin_edges[i + 1],
        center: histData.bin_centers[i],
      }));
      xDomain = [histData.bin_edges[0], histData.bin_edges[histData.bin_edges.length - 1]];
      yMax = Math.max(...histData.counts);
    } else if (variableData.all_values && variableData.all_values.length > 0) {
      const values = variableData.all_values.filter((v) => v !== null && !isNaN(v));
      if (values.length === 0) return;

      xDomain = d3.extent(values);
      const numBins = Math.min(30, Math.max(5, Math.ceil(Math.sqrt(values.length))));
      const histogram = d3.histogram().domain(xDomain).thresholds(numBins);
      const rawBins = histogram(values);
      bins = rawBins.map((bin) => ({
        count: bin.length,
        x0: bin.x0,
        x1: bin.x1,
        center: (bin.x0 + bin.x1) / 2,
      }));
      yMax = Math.max(...bins.map((b) => b.count));
    } else {
      return;
    }

    // ─── Smart x-axis label rotation ────────────────────────────────────
    // 1. Apply smartFormat first — turns 1,000,000 into "1M" etc.
    // 2. Estimate worst-case label width by sampling the domain.
    // 3. Compare against per-tick width budget; rotate only if labels would clash.
    // 4. Grow bottom margin to fit rotated labels and push axis title below them.
    const tickCount = isCompact ? 5 : 8;
    const sampleVals = [
      xDomain[0],
      xDomain[1],
      (xDomain[0] + xDomain[1]) / 2,
      xDomain[0] + (xDomain[1] - xDomain[0]) * 0.25,
      xDomain[0] + (xDomain[1] - xDomain[0]) * 0.75,
    ];
    const maxLabelChars = Math.max(...sampleVals.map((v) => smartFormat(v).length));
    const charWidth = 6.5; // ≈ 11px Outfit average
    const maxLabelPx = maxLabelChars * charWidth;
    const perTickBudget = innerWidth / tickCount;
    const shouldRotate = maxLabelPx > perTickBudget * 0.85;

    if (shouldRotate) {
      // Rotated labels stand vertical — their visual height = their text width.
      // 18px buffer keeps the axis title from kissing the rotated tick text.
      margin.bottom = Math.max(78, maxLabelPx + 28);
    }
    const innerHeight = height - margin.top - margin.bottom;

    const x = d3.scaleLinear().domain(xDomain).range([0, innerWidth]);
    const y = d3.scaleLinear().domain([0, yMax * 1.1]).range([innerHeight, 0]);

    // Pre-classify bins: which contain outliers? (used for both color and a11y)
    const outlierValues = variableData.outlier_values || [];
    bins.forEach((bin) => {
      bin.hasOutlier = outlierValues.some((o) => o >= bin.x0 && o < bin.x1);
    });

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Subtle gridlines
    g.append('g')
      .attr('class', 'dq-hist-grid')
      .call(d3.axisLeft(y).tickSize(-innerWidth).tickFormat(''))
      .call((sel) => {
        sel.selectAll('line')
          .style('stroke', isDarkMode ? '#374151' : '#E5E7EB')
          .style('stroke-dasharray', '2,3')
          .style('stroke-opacity', isDarkMode ? 0.35 : 0.6);
        sel.select('.domain').remove();
      });

    // Modern tooltip — single instance, theme-aware, soft shadow, glass blur
    const tooltip = d3.select('body').append('div')
      .attr('class', 'dq-histogram-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', isDarkMode ? 'rgba(17, 17, 20, 0.96)' : 'rgba(255, 255, 255, 0.98)')
      .style('color', isDarkMode ? '#F3F4F6' : '#111827')
      .style('padding', '10px 12px')
      .style('border', `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`)
      .style('border-radius', '10px')
      .style('box-shadow', isDarkMode
        ? '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 0 0.5px rgba(255, 255, 255, 0.04)'
        : '0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.02)')
      .style('font-size', '12px')
      .style('font-family', "'Outfit', sans-serif")
      .style('line-height', '1.5')
      .style('letter-spacing', '-0.005em')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('backdrop-filter', 'blur(8px)')
      .style('-webkit-backdrop-filter', 'blur(8px)')
      .style('transition', 'opacity 0.15s ease')
      .style('opacity', '0');

    // Bars — rounded top corners, animated entry from baseline
    const bars = g.selectAll('.dq-hist-bar')
      .data(bins)
      .enter()
      .append('rect')
      .attr('class', 'dq-hist-bar')
      .attr('x', (d) => x(d.x0))
      .attr('width', (d) => Math.max(0, x(d.x1) - x(d.x0) - 1))
      .attr('rx', 2)
      .attr('ry', 2)
      .attr('fill', (d) => d.hasOutlier ? PALETTE.outlierFill : PALETTE.normalFill)
      .attr('stroke', (d) => d.hasOutlier ? PALETTE.outlierStroke : PALETTE.normalStroke)
      .attr('stroke-width', (d) => d.hasOutlier ? 1.5 : 0.75)
      .attr('opacity', (d) => d.hasOutlier ? 0.85 : 0.65)
      .style('transition', 'opacity 0.15s ease, filter 0.15s ease')
      // Start collapsed at baseline, then animate up
      .attr('y', innerHeight)
      .attr('height', 0);

    bars.transition()
      .duration(550)
      .ease(d3.easeCubicOut)
      .delay((d, i) => Math.min(i * 12, 300))
      .attr('y', (d) => y(d.count))
      .attr('height', (d) => innerHeight - y(d.count));

    // Hover interactions
    bars
      .on('mouseover', function (event, d) {
        d3.select(this)
          .attr('opacity', 1)
          .style('filter', 'brightness(1.08)');
        const total = variableData.total_valid_observations || bins.reduce((a, b) => a + b.count, 0);
        const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : '0.0';
        tooltip
          .style('visibility', 'visible')
          .style('opacity', '1')
          .html(`
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${d.hasOutlier ? PALETTE.outlierFill : PALETTE.normalFill};"></span>
              <span style="font-weight:600;font-size:11px;letter-spacing:0.04em;text-transform:uppercase;color:${isDarkMode ? '#9CA3AF' : '#6B7280'};">${d.hasOutlier ? 'Outlier bin' : 'In range'}</span>
            </div>
            <div style="font-variant-numeric:tabular-nums;"><span style="color:${isDarkMode ? '#9CA3AF' : '#6B7280'};">Range</span> &nbsp; ${smartFormat(d.x0)} – ${smartFormat(d.x1)}</div>
            <div style="font-variant-numeric:tabular-nums;"><span style="color:${isDarkMode ? '#9CA3AF' : '#6B7280'};">Count</span> &nbsp; <strong>${d.count}</strong></div>
            <div style="font-variant-numeric:tabular-nums;"><span style="color:${isDarkMode ? '#9CA3AF' : '#6B7280'};">Frequency</span> &nbsp; ${pct}%</div>
          `);
      })
      .on('mousemove', function (event) {
        tooltip
          .style('top', event.pageY - 12 + 'px')
          .style('left', event.pageX + 14 + 'px');
      })
      .on('mouseout', function (event, d) {
        d3.select(this)
          .attr('opacity', d.hasOutlier ? 0.85 : 0.65)
          .style('filter', 'none');
        tooltip.style('opacity', '0').style('visibility', 'hidden');
      });

    // Statistical reference lines (mean, median) — pill labels positioned at top
    const refLines = [
      { key: 'mean', label: 'Mean', color: PALETTE.meanLine, dash: '5,4' },
      { key: 'median', label: 'Median', color: PALETTE.medianLine, dash: '3,3' },
    ];

    refLines.forEach(({ key, label, color, dash }) => {
      const value = variableData[key];
      if (value === undefined || value === null || isNaN(value)) return;
      // Skip if outside the visible domain
      if (value < xDomain[0] || value > xDomain[1]) return;

      const lineX = x(value);

      g.append('line')
        .attr('x1', lineX)
        .attr('x2', lineX)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', dash)
        .attr('opacity', 0)
        .transition()
        .delay(450)
        .duration(300)
        .attr('opacity', 0.85);

      // Pill label at the top of the line
      const labelText = `${label} ${smartFormat(value)}`;
      const labelGroup = g.append('g')
        .attr('transform', `translate(${lineX}, -8)`)
        .attr('opacity', 0);

      // Approximate width — D3 lacks measured bbox until rendered, so estimate from char count
      const approxLabelWidth = labelText.length * 5.6 + 14;
      const labelHeight = 16;

      labelGroup.append('rect')
        .attr('x', -approxLabelWidth / 2)
        .attr('y', -labelHeight)
        .attr('width', approxLabelWidth)
        .attr('height', labelHeight)
        .attr('rx', 8)
        .attr('ry', 8)
        .attr('fill', color)
        .attr('opacity', 0.92);

      labelGroup.append('text')
        .attr('x', 0)
        .attr('y', -4)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('font-family', "'Outfit', sans-serif")
        .style('fill', '#FFFFFF')
        .style('letter-spacing', '0.01em')
        .text(labelText);

      labelGroup.transition()
        .delay(550)
        .duration(300)
        .attr('opacity', 1);
    });

    // Axes — clean, modern. X-axis uses smartFormat and rotates when labels would clash.
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(x)
          .ticks(tickCount)
          .tickSize(4)
          .tickPadding(shouldRotate ? 8 : 6)
          .tickFormat(smartFormat)
      );

    if (shouldRotate) {
      xAxis.selectAll('text')
        .attr('transform', 'rotate(-90)')
        .attr('text-anchor', 'end')
        .attr('dx', '-8')
        .attr('dy', '-3');
    }

    const yAxis = g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(4).tickPadding(6).tickFormat(smartFormat));

    [xAxis, yAxis].forEach((axis) => {
      axis.selectAll('text')
        .style('font-size', '11px')
        .style('font-family', "'Outfit', sans-serif")
        .style('fill', isDarkMode ? '#9CA3AF' : '#6B7280');
      axis.selectAll('path.domain')
        .style('stroke', isDarkMode ? '#374151' : '#E5E7EB')
        .style('stroke-width', 1);
      axis.selectAll('.tick line')
        .style('stroke', isDarkMode ? '#374151' : '#E5E7EB');
    });

    // Axis labels
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -margin.left + 14)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', '500')
      .style('font-family', "'Outfit', sans-serif")
      .style('letter-spacing', '0.06em')
      .style('text-transform', 'uppercase')
      .style('fill', isDarkMode ? '#6B7280' : '#9CA3AF')
      .text('Frequency');

    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + (shouldRotate ? maxLabelPx + 18 : 40))
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('font-family', "'Outfit', sans-serif")
      .style('fill', isDarkMode ? '#9CA3AF' : '#4B5563')
      .text(selectedVariable);

    return () => {
      tooltip.remove();
    };
  }, [data, selectedVariable, width, height, isDarkMode, isCompact]);

  return (
    <div ref={containerRef} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <svg ref={ref} width={width} height={height} style={{ display: 'block', maxWidth: '100%' }} />
    </div>
  );
};

export default HistogramChart;